import React, { useState, useRef } from "react";
import { Scanner } from "@yudiel/react-qr-scanner";
import {
  FaQrcode,
  FaTimes,
  FaSpinner,
  FaExclamationTriangle,
  FaCheckDouble,
  FaBarcode,
  FaPlus,
} from "react-icons/fa";

const QrScannerModal = ({ onClose, onDetect }) => {
  const [flash, setFlash] = useState(null);
  const [manualCode, setManualCode] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [scanCount, setScanCount] = useState(0);

  const isProcessingRef = useRef(false);
  const timeoutRef = useRef(null);

  // Vibration helper
  const triggerSuccessVibration = () => {
    if (navigator.vibrate) {
      // Short double vibration for success feel
      navigator.vibrate([50, 30, 50]);
    }
  };

  const handleScan = async (result) => {
    if (!result || !result[0] || isProcessingRef.current) return;

    const code = result[0].rawValue?.trim();
    if (!code) return;

    isProcessingRef.current = true;

    setFlash({ tone: "pending", message: `Processing ${code}…` });

    try {
      const ok = await onDetect(code);

      if (ok) {
        triggerSuccessVibration(); // ← Vibration on success
        setScanCount((prev) => prev + 1);

        setFlash({
          tone: "success",
          message: `✓ Added • ${code}`,
        });

        // Auto clear after short delay
        timeoutRef.current = setTimeout(() => {
          setFlash(null);
          isProcessingRef.current = false;
        }, 1100);
      } else {
        setFlash({
          tone: "error",
          message: `✗ No match for ${code}`,
        });
        timeoutRef.current = setTimeout(() => {
          setFlash(null);
          isProcessingRef.current = false;
        }, 1600);
      }
    } catch (err) {
      setFlash({ tone: "error", message: "Error processing item" });
      timeoutRef.current = setTimeout(() => {
        setFlash(null);
        isProcessingRef.current = false;
      }, 1800);
    }
  };

  const handleError = (err) => {
    console.error("Scanner error:", err);
    if (!errorMsg) {
      setErrorMsg("Camera active. Point at codes to scan.");
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;

    const code = manualCode.trim();
    isProcessingRef.current = true;

    setFlash({ tone: "pending", message: `Processing ${code}…` });

    try {
      const ok = await onDetect(code);

      if (ok) {
        triggerSuccessVibration();
        setScanCount((prev) => prev + 1);
        setFlash({ tone: "success", message: `✓ Added • ${code}` });
      } else {
        setFlash({ tone: "error", message: `✗ No match for ${code}` });
      }
      setManualCode("");
    } catch (err) {
      setFlash({ tone: "error", message: "Error processing code" });
    } finally {
      setTimeout(() => {
        setFlash(null);
        isProcessingRef.current = false;
      }, 1400);
    }
  };

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[6000] flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-md"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-5 shadow-2xl text-slate-100 flex flex-col gap-4">
        {/* Header */}
        <div className="flex justify-between items-center pb-3 border-b border-white/5">
          <div className="flex items-center gap-2">
            <FaQrcode className="text-emerald-400" />
            <h3 className="text-sm font-bold text-white">Scan Products</h3>
            {scanCount > 0 && (
              <span className="text-emerald-400 text-xs bg-emerald-900/60 px-2.5 py-0.5 rounded-full">
                {scanCount} added
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Scanner Area */}
        <div className="relative aspect-square w-full rounded-xl overflow-hidden border border-white/10 bg-slate-950">
          <Scanner
            onScan={handleScan}
            onError={handleError}
            constraints={{ facingMode: "environment" }}
            scanDelay={280}
            formats={[
              "qr_code",
              "code_128",
              "ean_13",
              "ean_8",
              "upc_a",
              "code_39",
              "code_93",
            ]}
            className="w-full h-full"
          />

          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-64 h-64 border-2 border-emerald-400/60 rounded-2xl" />
          </div>

          <div className="absolute top-6 left-6 w-10 h-10 border-t-4 border-l-4 border-emerald-400 rounded-tl-xl pointer-events-none" />
          <div className="absolute top-6 right-6 w-10 h-10 border-t-4 border-r-4 border-emerald-400 rounded-tr-xl pointer-events-none" />
          <div className="absolute bottom-6 left-6 w-10 h-10 border-b-4 border-l-4 border-emerald-400 rounded-bl-xl pointer-events-none" />
          <div className="absolute bottom-6 right-6 w-10 h-10 border-b-4 border-r-4 border-emerald-400 rounded-br-xl pointer-events-none" />
        </div>

        {/* Feedback */}
        {flash && (
          <div
            className={`flex items-center gap-3 text-sm px-4 py-3 rounded-2xl border ${
              flash.tone === "success"
                ? "bg-emerald-900/60 border-emerald-500/60 text-emerald-300"
                : flash.tone === "error"
                ? "bg-red-900/60 border-red-500/60 text-red-300"
                : "bg-amber-900/60 border-amber-500/60 text-amber-300"
            }`}
          >
            {flash.tone === "success" ? (
              <FaCheckDouble className="text-xl" />
            ) : flash.tone === "error" ? (
              <FaExclamationTriangle className="text-xl" />
            ) : (
              <FaSpinner className="animate-spin text-xl" />
            )}
            <span>{flash.message}</span>
          </div>
        )}

        {errorMsg && (
          <p className="text-red-400 text-center text-sm">{errorMsg}</p>
        )}

        {/* Manual Entry */}
        <form onSubmit={handleManualSubmit} className="flex flex-col gap-1.5">
          <label className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-2">
            <FaBarcode /> Manual Entry
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="Type barcode or QR code..."
              className="flex-1 bg-slate-950 border border-white/10 rounded-xl px-4 py-3 text-white font-mono focus:border-emerald-500 focus:outline-none"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 rounded-xl font-medium flex items-center gap-2"
            >
              <FaPlus /> Add
            </button>
          </div>
        </form>

        <p className="text-center text-[10px] text-slate-500">
          Continuous scanning • Vibration on successful scan
        </p>
      </div>
    </div>
  );
};

export default QrScannerModal;
